import React, { useCallback, useEffect, useState } from 'react';
import { Network } from 'lucide-react';
import { toast } from 'sonner';
import DashboardLayout from '@/components/DashboardLayout';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { jsonApi } from '@/lib/jsonApi';
import AttributeCard from '@/components/taxonomy/AttributeCard';
import { AddAttributeForm } from '@/components/taxonomy/AddForms';
import SurveyApplicability from '@/components/taxonomy/SurveyApplicability';
import BackfillCard from '@/components/taxonomy/BackfillCard';
import TemplatePicker from '@/components/taxonomy/TemplatePicker';
import { SetupOrigin, ResetTaxonomy } from '@/components/taxonomy/SetupBits';

function useTaxonomy() {
  const [tax, setTax] = useState(null);
  const [error, setError] = useState(null);
  const reload = useCallback(() => jsonApi('/taxonomy').then(setTax).catch(setError), []);
  useEffect(() => { reload(); }, [reload]);
  const mutate = async (path, method, body, msg) => {
    try { await jsonApi(path, method, body); await reload(); if (msg) toast.success(msg); return true; }
    catch (e) { toast.error(e.message); return false; }
  };
  return { tax, error, mutate, reload };
}

const Counts = ({ attrs }) => {
  const live = attrs.filter((a) => a.status === 'active');
  const facs = live.flatMap((a) => a.factors);
  const n = (c) => facs.filter((f) => f.classification === c && f.status === 'active').length;
  return (
    <p className="text-xs text-[#64748B]" data-testid="taxonomy-counts">
      {live.length} active attributes · {n('positive_driver')} active Positive Drivers · {n('gap')} active Gaps
      {attrs.length > live.length ? ` · ${attrs.length - live.length} inactive attributes` : ''}
    </p>
  );
};

export default function TaxonomyManagement() {
  const { tax, error, mutate, reload } = useTaxonomy();
  const canEdit = !!tax?.can_edit;
  const attrs = tax?.attributes || [];
  const hasFactors = attrs.some((a) => a.status === 'active' && a.factors.some((f) => f.status === 'active'));
  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-6" data-testid="taxonomy-page">
        <header className="flex flex-col gap-2">
          <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#1A3E6F] flex items-center gap-1.5"><Network className="w-3.5 h-3.5" /> Feedback taxonomy</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#0F172A] font-['Montserrat'] tracking-tight">Attributes &amp; Factors</h1>
          <p className="text-sm sm:text-base text-[#475569] max-w-3xl">Your master list for tagging what customers talk about. Each Attribute holds Factors, and every Factor is either a Positive Driver or a Gap. Factors are never deleted — switch them off to stop using them.</p>
          {tax && !tax.needs_setup && <Counts attrs={attrs} />}
          {tax && <SetupOrigin setup={tax.setup} />}
        </header>
        {error && <p className="text-sm text-[#A1272E] bg-[#FFF0F0] border border-[#F0C6C9] rounded-xl px-4 py-3" data-testid="taxonomy-error">{error.message}</p>}
        {!tax && !error && <div className="py-20 text-center"><div className="loading-spinner mx-auto" /></div>}
        {tax?.needs_setup && <TemplatePicker canEdit={canEdit} onDone={reload} />}
        {tax && !tax.needs_setup && (
          <>
            <BackfillCard canEdit={canEdit} hasFactors={hasFactors} />
            <Tabs defaultValue="attributes">
              <TabsList data-testid="taxonomy-tabs">
                <TabsTrigger value="attributes" data-testid="taxonomy-tab-attributes">Attributes &amp; Factors</TabsTrigger>
                <TabsTrigger value="surveys" data-testid="taxonomy-tab-surveys">Survey applicability</TabsTrigger>
              </TabsList>
              <TabsContent value="attributes" className="space-y-5 mt-5">
                {canEdit && <AddAttributeForm onAdd={(b) => mutate('/taxonomy/attributes', 'POST', b, 'Attribute added')} />}
                {!attrs.length && <p className="text-sm text-[#94A3B8] text-center py-8" data-testid="taxonomy-empty">No attributes yet. Add your first one above.</p>}
                {attrs.map((a, i) => (
                  <AttributeCard key={a.attribute_id} attr={a} index={i} canEdit={canEdit}
                    onUpdateAttr={(id, b) => mutate(`/taxonomy/attributes/${id}`, 'PUT', b, 'Attribute updated')}
                    onUpdateFactor={(id, b) => mutate(`/taxonomy/factors/${id}`, 'PUT', b, 'Factor updated')}
                    onAddFactor={(b) => mutate('/taxonomy/factors', 'POST', b, 'Factor added')} />
                ))}
              </TabsContent>
              <TabsContent value="surveys" className="mt-5">
                <SurveyApplicability attributes={attrs} canEdit={canEdit} />
              </TabsContent>
            </Tabs>
            {tax.can_reset && <ResetTaxonomy onDone={reload} />}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
